/* ═══════════════════════════════════════════════════════════════════
   BLOQUE 14 DE 100 — SISTEMA DE GANADORES Y PREMIOS RFW
   Mock Data — 30 ganadores, 8 tipos, 5 insignias, stats globales
   ═══════════════════════════════════════════════════════════════════ */

/* ─── Winner Types ─── */
export type WinnerType =
  | 'concurso'
  | 'trivia'
  | 'encuesta'
  | 'evento'
  | 'club'
  | 'radio-vs-radio'
  | 'programa-vs-programa'
  | 'locutor-vs-locutor';

export const winnerTypeLabels: Record<WinnerType, string> = {
  concurso: 'Ganador Concurso',
  trivia: 'Ganador Trivia',
  encuesta: 'Ganador Encuesta',
  evento: 'Ganador Evento',
  club: 'Ganador Club',
  'radio-vs-radio': 'Ganador Radio vs Radio',
  'programa-vs-programa': 'Ganador Programa vs Programa',
  'locutor-vs-locutor': 'Ganador Locutor vs Locutor',
};

export const winnerTypeIcons: Record<WinnerType, string> = {
  concurso: 'ri-trophy-line',
  trivia: 'ri-question-answer-line',
  encuesta: 'ri-bar-chart-line',
  evento: 'ri-calendar-event-line',
  club: 'ri-group-line',
  'radio-vs-radio': 'ri-radio-line',
  'programa-vs-programa': 'ri-calendar-check-line',
  'locutor-vs-locutor': 'ri-mic-line',
};

export const winnerTypeColors: Record<WinnerType, string> = {
  concurso: 'text-amber-600 bg-amber-100',
  trivia: 'text-violet-600 bg-violet-100',
  encuesta: 'text-sky-600 bg-sky-100',
  evento: 'text-emerald-600 bg-emerald-100',
  club: 'text-rose-600 bg-rose-100',
  'radio-vs-radio': 'text-orange-600 bg-orange-100',
  'programa-vs-programa': 'text-teal-600 bg-teal-100',
  'locutor-vs-locutor': 'text-fuchsia-600 bg-fuchsia-100',
};

/* ─── Winner Badge ─── */
export type WinnerBadge =
  | 'primer-ganador'
  | 'ganador-semanal'
  | 'ganador-mensual'
  | 'campeon-rfw'
  | 'leyenda-rfw';

export interface WinnerBadgeConfig {
  id: WinnerBadge;
  name: string;
  description: string;
  icon: string;
  emoji: string;
  color: string;
  bg: string;
  border: string;
  threshold: string;
  scoreBonus: number;
}

export const winnerBadgeConfigs: WinnerBadgeConfig[] = [
  {
    id: 'primer-ganador',
    name: 'Primer Ganador',
    description: 'Tu primera victoria en cualquier competencia RFW. El inicio de tu leyenda.',
    icon: 'ri-award-line',
    emoji: '🌱',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    threshold: '1 victoria',
    scoreBonus: 50,
  },
  {
    id: 'ganador-semanal',
    name: 'Ganador Semanal',
    description: 'Campeón de un torneo semanal. Consistencia que brilla.',
    icon: 'ri-medal-line',
    emoji: '⚡',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    threshold: 'Victoria semanal',
    scoreBonus: 100,
  },
  {
    id: 'ganador-mensual',
    name: 'Ganador Mensual',
    description: 'Dominaste todo un mes de competencia. Elite absoluta.',
    icon: 'ri-vip-diamond-line',
    emoji: '💎',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-300',
    threshold: 'Victoria mensual',
    scoreBonus: 250,
  },
  {
    id: 'campeon-rfw',
    name: 'Campeón RFW',
    description: 'Ganador de un torneo anual o competencia mayor. Tu nombre queda en la historia.',
    icon: 'ri-shield-star-line',
    emoji: '🏆',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-300',
    threshold: 'Victoria anual',
    scoreBonus: 500,
  },
  {
    id: 'leyenda-rfw',
    name: 'Leyenda RFW',
    description: 'Múltiples campeonatos. Tu legado es eterno en la comunidad RFW.',
    icon: 'ri-vip-crown-line',
    emoji: '👑',
    color: 'text-yellow-500',
    bg: 'bg-yellow-50',
    border: 'border-yellow-400',
    threshold: '3+ victorias anuales',
    scoreBonus: 1000,
  },
];

/* ─── Winner Profile ─── */
export interface RfwWinner {
  id: number;
  type: WinnerType;
  name: string;
  photoUrl: string;
  city: string;
  radioName: string;
  programName: string;
  contestName: string;
  position: number;
  prize: string;
  prizeDescription: string;
  date: string;
  scoreEarned: number;
  badges: WinnerBadge[];
  featured: boolean;
  wallType: ('landing' | 'app' | 'radio' | 'programa' | 'club')[];
}

/* ─── Winner Wall Type ─── */
export type WallType = 'landing' | 'app' | 'radio' | 'programa' | 'club';

export const wallTypeLabels: Record<WallType, string> = {
  landing: 'Landing',
  app: 'App',
  radio: 'Radio',
  programa: 'Programa',
  club: 'Club',
};

export const wallTypeIcons: Record<WallType, string> = {
  landing: 'ri-home-line',
  app: 'ri-smartphone-line',
  radio: 'ri-radio-line',
  programa: 'ri-calendar-check-line',
  club: 'ri-group-line',
};

/* ─── Time Filter ─── */
export type WinnerTimeFilter = 'hoy' | 'semana' | 'mes' | 'ano' | 'historico';

export const winnerTimeFilterLabels: Record<WinnerTimeFilter, string> = {
  hoy: 'Hoy',
  semana: 'Semana',
  mes: 'Mes',
  ano: 'Año',
  historico: 'Histórico',
};

export const winnerTimeFilterIcons: Record<WinnerTimeFilter, string> = {
  hoy: 'ri-calendar-line',
  semana: 'ri-calendar-2-line',
  mes: 'ri-calendar-check-line',
  ano: 'ri-calendar-todo-line',
  historico: 'ri-history-line',
};

/* ═══════════════════════════════════════════════════════════════════
   WINNER PHOTOS — Stable Diffusion prompts for realistic portraits
   ═══════════════════════════════════════════════════════════════════ */

const winnerPhotos: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latino%20man%20in%20his%20mid%2020s%20with%20warm%20genuine%20smile%2C%20dark%20wavy%20hair%2C%20clean%20modern%20casual%20shirt%2C%20studio%20lighting%20with%20soft%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20confident%20and%20approachable%20expression%2C%20high%20detail%20headshot&width=300&height=300&seq=winner-photo-1&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20late%2020s%20with%20radiant%20confident%20smile%2C%20long%20dark%20hair%2C%20elegant%20modern%20blouse%2C%20soft%20studio%20lighting%20with%20neutral%20beige%20background%2C%20editorial%20portrait%20photography%2C%20warm%20and%20charismatic%20expression%2C%20high%20detail&width=300&height=300&seq=winner-photo-2&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20early%2030s%20with%20charismatic%20smile%2C%20short%20dark%20hair%20styled%20neatly%2C%20smart%20casual%20blazer%2C%20warm%20studio%20lighting%20with%20neutral%20gray%20background%2C%20editorial%20portrait%20photography%2C%20friendly%20and%20professional%20look%2C%20high%20detail&width=300&height=300&seq=winner-photo-3&orientation=squarish',
  4: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latina%20woman%20in%20her%20early%2020s%20with%20bright%20energetic%20smile%2C%20curly%20dark%20hair%2C%20colorful%20casual%20top%2C%20vibrant%20studio%20lighting%20with%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20youthful%20and%20enthusiastic%20expression%2C%20high%20detail&width=300&height=300&seq=winner-photo-4&orientation=squarish',
  5: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20late%2030s%20with%20distinguished%20confident%20look%2C%20salt%20and%20pepper%20short%20beard%2C%20business%20casual%20shirt%2C%20dramatic%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20experienced%20and%20authoritative%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-5&orientation=squarish',
  6: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20mid%2030s%20with%20warm%20genuine%20expression%2C%20shoulder%20length%20dark%20hair%2C%20stylish%20casual%20sweater%2C%20soft%20natural%20window%20lighting%20with%20neutral%20warm%20background%2C%20editorial%20portrait%20photography%2C%20approachable%20and%20kind%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-6&orientation=squarish',
  7: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latino%20man%20in%20his%20late%20teens%20with%20fresh%20energetic%20smile%2C%20styled%20dark%20hair%2C%20casual%20streetwear%20hoodie%2C%20vibrant%20studio%20lighting%20with%20neutral%20white%20background%2C%20editorial%20portrait%20photography%2C%20youthful%20cool%20vibe%2C%20high%20detail&width=300&height=300&seq=winner-photo-7&orientation=squarish',
  8: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20late%2030s%20with%20sophisticated%20confident%20smile%2C%20elegant%20dark%20hair%20styled%20up%2C%20professional%20blazer%2C%20refined%20studio%20lighting%20with%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20accomplished%20and%20graceful%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-8&orientation=squarish',
  9: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20early%2040s%20with%20warm%20paternal%20smile%2C%20neat%20graying%20hair%2C%20casual%20button-down%20shirt%2C%20soft%20natural%20lighting%20with%20neutral%20beige%20background%2C%20editorial%20portrait%20photography%2C%20experienced%20and%20trustworthy%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-9&orientation=squarish',
  10: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latina%20woman%20in%20her%20mid%2020s%20with%20artistic%20creative%20expression%2C%20short%20trendy%20haircut%2C%20colorful%20accessories%2C%20modern%20studio%20lighting%20with%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20creative%20and%20bold%20personality%2C%20high%20detail&width=300&height=300&seq=winner-photo-10&orientation=squarish',
  11: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20mid%2020s%20with%20friendly%20approachable%20smile%2C%20clean%20shaven%20look%2C%20casual%20denim%20shirt%2C%20balanced%20studio%20lighting%20with%20neutral%20gray%20background%2C%20editorial%20portrait%20photography%2C%20relatable%20and%20down-to-earth%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-11&orientation=squarish',
  12: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20early%2030s%20with%20intellectual%20warm%20expression%2C%20glasses%2C%20dark%20hair%20in%20professional%20bob%20cut%2C%20smart%20casual%20blouse%2C%20soft%20studio%20lighting%20with%20neutral%20warm%20background%2C%20editorial%20portrait%20photography%2C%20intelligent%20and%20thoughtful%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-12&orientation=squarish',
  13: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20late%2020s%20with%20passionate%20intense%20expression%2C%20athletic%20build%20visible%2C%20fitted%20polo%20shirt%2C%20dynamic%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20competitive%20and%20driven%20energy%2C%20high%20detail&width=300&height=300&seq=winner-photo-13&orientation=squarish',
  14: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latina%20woman%20in%20her%20early%2020s%20with%20sweet%20genuine%20smile%2C%20natural%20minimal%20makeup%2C%20cozy%20knit%20sweater%2C%20warm%20golden%20hour%20lighting%20with%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20wholesome%20and%20genuine%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-14&orientation=squarish',
  15: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20late%2040s%20with%20wise%20experienced%20smile%2C%20distinguished%20gray%20hair%20and%20beard%2C%20classic%20vest%20outfit%2C%20rich%20warm%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20veteran%20mentor%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-15&orientation=squarish',
  16: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20late%2020s%20with%20fierce%20confident%20look%2C%20long%20sleek%20dark%20hair%2C%20bold%20red%20blouse%2C%20dramatic%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20powerful%20and%20determined%20energy%2C%20high%20detail&width=300&height=300&seq=winner-photo-16&orientation=squarish',
  17: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latino%20man%20in%20his%20early%2020s%20with%20enthusiastic%20joyful%20smile%2C%20curly%20dark%20hair%2C%20bright%20colorful%20shirt%2C%20energetic%20studio%20lighting%20with%20neutral%20white%20background%2C%20editorial%20portrait%20photography%2C%20positive%20and%20infectious%20energy%2C%20high%20detail&width=300&height=300&seq=winner-photo-17&orientation=squarish',
  18: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20early%2040s%20with%20elegant%20mature%20beauty%2C%20sophisticated%20silver%20streak%20in%20dark%20hair%2C%20chic%20blouse%2C%20refined%20studio%20lighting%20with%20neutral%20warm%20background%2C%20editorial%20portrait%20photography%2C%20graceful%20and%20experienced%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-18&orientation=squarish',
  19: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20early%2020s%20with%20creative%20artistic%20vibe%2C%20alternative%20style%20with%20subtle%20facial%20piercings%2C%20graphic%20t-shirt%2C%20moody%20studio%20lighting%20with%20neutral%20gray%20background%2C%20editorial%20portrait%20photography%2C%20edgy%20and%20unique%20personality%2C%20high%20detail&width=300&height=300&seq=winner-photo-19&orientation=squarish',
  20: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20mid%2030s%20with%20warm%20maternal%20smile%2C%20soft%20curls%20framing%20face%2C%20comfortable%20casual%20top%2C%20gentle%20natural%20lighting%20with%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20nurturing%20and%20kind%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-20&orientation=squarish',
  21: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20late%2030s%20with%20urban%20cool%20style%2C%20well-groomed%20short%20beard%2C%20stylish%20bomber%20jacket%2C%20cinematic%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20confident%20and%20street-smart%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-21&orientation=squarish',
  22: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latina%20woman%20in%20her%20late%20teens%20with%20fresh%20innocent%20smile%2C%20natural%20braided%20hair%2C%20simple%20white%20top%2C%20bright%20airy%20studio%20lighting%20with%20neutral%20white%20background%2C%20editorial%20portrait%20photography%2C%20pure%20and%20hopeful%20expression%2C%20high%20detail&width=300&height=300&seq=winner-photo-22&orientation=squarish',
  23: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20mid%2040s%20with%20dignified%20professional%20look%2C%20polished%20appearance%2C%20tailored%20suit%20jacket%2C%20executive%20studio%20lighting%20with%20neutral%20gray%20background%2C%20editorial%20portrait%20photography%2C%20accomplished%20and%20respected%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-23&orientation=squarish',
  24: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20early%2030s%20with%20determined%20ambitious%20expression%2C%20sleek%20dark%20hair%20pulled%20back%2C%20power%20blazer%2C%20sharp%20studio%20lighting%20with%20neutral%20white%20background%2C%20editorial%20portrait%20photography%2C%20focused%20and%20driven%20energy%2C%20high%20detail&width=300&height=300&seq=winner-photo-24&orientation=squarish',
  25: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20mid%2050s%20with%20grandfatherly%20warm%20smile%2C%20full%20gray%20beard%2C%20comfortable%20cardigan%2C%20soft%20natural%20lighting%20with%20neutral%20warm%20background%2C%20editorial%20portrait%20photography%2C%20beloved%20community%20elder%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-25&orientation=squarish',
  26: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20latina%20woman%20in%20her%20mid%2020s%20with%20outgoing%20bubbly%20personality%2C%20voluminous%20curly%20hair%2C%20bright%20casual%20outfit%2C%20fun%20vibrant%20studio%20lighting%20with%20neutral%20cream%20background%2C%20editorial%20portrait%20photography%2C%20life%20of%20the%20party%20energy%2C%20high%20detail&width=300&height=300&seq=winner-photo-26&orientation=squarish',
  27: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latino%20man%20in%20his%20early%2030s%20with%20focused%20determined%20look%2C%20athletic%20gym%20wear%20visible%2C%20intense%20dramatic%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20competitive%20champion%20energy%2C%20high%20detail&width=300&height=300&seq=winner-photo-27&orientation=squarish',
  28: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%20late%2020s%20with%20charismatic%20performer%20smile%2C%20dramatic%20eye%20makeup%2C%20glamorous%20evening%20top%2C%20theatrical%20studio%20lighting%20with%20neutral%20dark%20background%2C%20editorial%20portrait%20photography%2C%20star%20quality%20presence%2C%20high%20detail&width=300&height=300&seq=winner-photo-28&orientation=squarish',
};

/* ═══════════════════════════════════════════════════════════════════
   30 WINNERS — 8 tipos, datos realistas, fechas progresivas
   ═══════════════════════════════════════════════════════════════════ */

export const allWinners: RfwWinner[] = [
  /* ─── HOY (3 ganadores) ─── */
  {
    id: 1, type: 'trivia', name: 'Alejandro Vargas', photoUrl: winnerPhotos[1],
    city: 'Houston, TX', radioName: 'La Mexicana FM', programName: 'El Desparche',
    contestName: 'Trivia de la Mañana: Éxitos de los 90s', position: 1,
    prize: 'Kit RFW Premium', prizeDescription: 'Audífonos profesionales + Membresía 3 meses + 250 pts',
    date: '2026-06-21', scoreEarned: 250,
    badges: ['primer-ganador'],
    featured: true,
    wallType: ['landing', 'app', 'radio', 'programa'],
  },
  {
    id: 2, type: 'concurso', name: 'María Fernanda Castillo', photoUrl: winnerPhotos[2],
    city: 'San Antonio, TX', radioName: 'Banda Radio MX', programName: 'Banda en Vivo',
    contestName: 'Concurso La Mejor Canción Original', position: 1,
    prize: 'Grabación en Estudio Profesional', prizeDescription: 'Sesión de 8h en estudio + producción + difusión en 12 radios RFW',
    date: '2026-06-21', scoreEarned: 800,
    badges: ['ganador-mensual', 'primer-ganador'],
    featured: true,
    wallType: ['landing', 'app', 'radio', 'programa'],
  },
  {
    id: 3, type: 'club', name: 'Ricardo Mendoza Sánchez', photoUrl: winnerPhotos[3],
    city: 'Dallas, TX', radioName: 'Deportes Total Radio', programName: 'Deporte Total',
    contestName: 'Reto de Fidelidad: Club Deportes Total Fanáticos', position: 1,
    prize: 'Viaje al Super Bowl + 500 pts RFW', prizeDescription: 'Paquete VIP todo incluido al Super Bowl 2027 + 500 puntos al Score',
    date: '2026-06-21', scoreEarned: 500,
    badges: ['ganador-semanal', 'primer-ganador'],
    featured: true,
    wallType: ['landing', 'club'],
  },
  /* ─── ESTA SEMANA (8 ganadores) ─── */
  {
    id: 4, type: 'radio-vs-radio', name: 'Banda Radio MX', photoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20official%20shield%20emblem%2C%20vibrant%20red%20white%20and%20green%20colors%2C%20professional%20broadcast%20logo%20design%20with%20trumpet%20and%20accordion%20elements%2C%20circular%20badge%20style%2C%20official%20station%20identity%20mark%2C%20polished%20vector%20design&width=300&height=300&seq=winner-entity-banda-mx&orientation=squarish',
    city: 'San Antonio, TX', radioName: 'Banda Radio MX', programName: '',
    contestName: 'Batalla de las Californias: Banda Radio MX vs Rock Total Radio', position: 1,
    prize: 'Campeón Semanal + Rey de California', prizeDescription: 'Badge Campeón Semanal + 250 puntos extra + posición destacada en landing por 7 días',
    date: '2026-06-20', scoreEarned: 250,
    badges: ['ganador-semanal'],
    featured: true,
    wallType: ['landing', 'radio'],
  },
  {
    id: 5, type: 'encuesta', name: 'Sofía Gutiérrez López', photoUrl: winnerPhotos[4],
    city: 'Santa Rosa, CA', radioName: 'Radio Nueva Actitud', programName: 'Tardes con Vale',
    contestName: 'Encuesta: ¿Cuál es el mejor programa de la tarde?', position: 1,
    prize: 'Mención al Aire + 100 pts', prizeDescription: 'Tu nombre mencionado en vivo por Valentina Ríos + 100 puntos al Score RFW',
    date: '2026-06-19', scoreEarned: 100,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['app', 'programa'],
  },
  {
    id: 6, type: 'locutor-vs-locutor', name: 'Diego Quintero', photoUrl: winnerPhotos[5],
    city: 'Dallas, TX', radioName: 'Deportes Total Radio', programName: 'Deporte Total',
    contestName: 'Duelo de Titanes del Micrófono: Diego Quintero vs Ricky Navarro', position: 1,
    prize: 'Voz de la Semana + Micrófono de Oro', prizeDescription: 'Badge Voz de la Semana + 200 puntos al Score + entrevista destacada en landing',
    date: '2026-06-18', scoreEarned: 200,
    badges: ['ganador-semanal'],
    featured: true,
    wallType: ['landing', 'radio', 'programa'],
  },
  {
    id: 7, type: 'concurso', name: 'Andrea Ramírez Torres', photoUrl: winnerPhotos[6],
    city: 'Chicago, IL', radioName: 'Radio Comunitaria Unida', programName: 'Modo Creativo',
    contestName: 'Concurso Cover Virtual 2026', position: 1,
    prize: 'Sesión de Fotos Profesional + 300 pts', prizeDescription: 'Sesión fotográfica profesional + 300 puntos al Score + perfil destacado',
    date: '2026-06-17', scoreEarned: 300,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['app', 'radio'],
  },
  {
    id: 8, type: 'evento', name: 'José Luis Hernández', photoUrl: winnerPhotos[7],
    city: 'Los Ángeles, CA', radioName: 'Rock Total Radio', programName: '',
    contestName: 'Festival de Bandas en Vivo 2026: Mejor Performance', position: 1,
    prize: 'Apertura en Próximo Festival + 400 pts', prizeDescription: 'Tu banda abre el próximo Festival RFW + 400 puntos + badge Evento Ganador',
    date: '2026-06-16', scoreEarned: 400,
    badges: ['ganador-semanal'],
    featured: false,
    wallType: ['landing', 'app', 'radio'],
  },
  {
    id: 9, type: 'programa-vs-programa', name: 'Tardes con Vale', photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20afternoon%20talk%20show%20concept%20art%20with%20golden%20hour%20lighting%2C%20cozy%20radio%20studio%20aesthetic%2C%20microphone%20and%20coffee%20cup%20on%20desk%2C%20soft%20peach%20and%20amber%20tones%2C%20intimate%20broadcast%20environment%2C%20inviting%20warm%20atmosphere%2C%20editorial%20style&width=300&height=300&seq=winner-entity-tardes-vale&orientation=squarish',
    city: 'Santa Rosa, CA', radioName: 'Radio Nueva Actitud', programName: 'Tardes con Vale',
    contestName: 'La Guerra del Rating: Tardes con Vale vs El Desparche', position: 1,
    prize: 'Programa Estrella + Rey del Rating', prizeDescription: 'Badge Programa Estrella + 200 puntos + destacado en homepage por 7 días',
    date: '2026-06-16', scoreEarned: 200,
    badges: ['ganador-semanal'],
    featured: true,
    wallType: ['landing', 'radio', 'programa'],
  },
  {
    id: 10, type: 'trivia', name: 'Carlos Eduardo Núñez', photoUrl: winnerPhotos[8],
    city: 'Miami, FL', radioName: 'Radio Fe y Esperanza', programName: 'Mañanas Argentinas',
    contestName: 'Trivia Bíblica: Personajes del Antiguo Testamento', position: 1,
    prize: 'Libro de Colección + 150 pts', prizeDescription: 'Biblia de colección edición especial + 150 puntos al Score RFW',
    date: '2026-06-15', scoreEarned: 150,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['app', 'radio', 'programa'],
  },
  {
    id: 11, type: 'club', name: 'Ana Patricia Morales', photoUrl: winnerPhotos[9],
    city: 'Phoenix, AZ', radioName: 'La Kalle 99.9', programName: '',
    contestName: 'Batalla de Ejércitos: Banda MX Army vs La Mexicana Fan Club', position: 2,
    prize: 'Subcampeón Club + Badge Plata', prizeDescription: 'Reconocimiento de subcampeón + badge Ejército de Plata + 100 puntos',
    date: '2026-06-15', scoreEarned: 100,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['club'],
  },
  /* ─── ESTE MES (7 ganadores) ─── */
  {
    id: 12, type: 'radio-vs-radio', name: 'La Mexicana FM', photoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20official%20emblem%20with%20guitar%20and%20sombrero%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20professional%20broadcast%20identity%20badge%2C%20festive%20vibrant%20style%2C%20polished%20vector%20emblem%20design&width=300&height=300&seq=winner-entity-mexicana-fm&orientation=squarish',
    city: 'Houston, TX', radioName: 'La Mexicana FM', programName: '',
    contestName: 'Duelo de Gigantes Latinos: La Mexicana FM vs Radio Nueva Actitud', position: 1,
    prize: 'Campeón Semanal + Gigante Latino + 250 pts', prizeDescription: 'Badge Gigante Latino + 250 puntos extra + mención en todas las radios de la red',
    date: '2026-06-10', scoreEarned: 250,
    badges: ['ganador-semanal'],
    featured: false,
    wallType: ['landing', 'radio'],
  },
  {
    id: 13, type: 'concurso', name: 'Fernando Castillo Rojas', photoUrl: winnerPhotos[10],
    city: 'Orlando, FL', radioName: 'Radio Vida Nueva', programName: '',
    contestName: 'Concurso Locutor Revelación 2026', position: 1,
    prize: 'Contrato de 6 meses como Locutor + 500 pts', prizeDescription: 'Contrato profesional como locutor junior por 6 meses + 500 puntos + mentoría',
    date: '2026-06-08', scoreEarned: 500,
    badges: ['ganador-mensual', 'primer-ganador'],
    featured: false,
    wallType: ['landing', 'app', 'radio'],
  },
  {
    id: 14, type: 'evento', name: 'Gabriela Montero Díaz', photoUrl: winnerPhotos[11],
    city: 'New York, NY', radioName: 'Noticias 24/7 Radio', programName: '',
    contestName: 'Maratón Deportivo Total: Ganadora Sorteo', position: 1,
    prize: 'Smart TV 55" + 300 pts', prizeDescription: 'Televisor Smart 4K 55 pulgadas + 300 puntos + suscripción premium 1 año',
    date: '2026-06-05', scoreEarned: 300,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['landing', 'app', 'radio'],
  },
  {
    id: 15, type: 'encuesta', name: 'Miguel Ángel Suárez', photoUrl: winnerPhotos[12],
    city: 'Austin, TX', radioName: 'Radio Voz del Pueblo', programName: '',
    contestName: 'Encuesta Nacional: La Voz de la Comunidad', position: 1,
    prize: 'Tablet + Entrevista en Radio + 200 pts', prizeDescription: 'Tablet última generación + entrevista en vivo + 200 puntos al Score',
    date: '2026-06-03', scoreEarned: 200,
    badges: ['ganador-mensual'],
    featured: false,
    wallType: ['app', 'radio'],
  },
  {
    id: 16, type: 'locutor-vs-locutor', name: 'Valentina Ríos', photoUrl: winnerPhotos[13],
    city: 'Santa Rosa, CA', radioName: 'Radio Nueva Actitud', programName: 'Tardes con Vale',
    contestName: 'Experiencia vs Juventud: Valentina Ríos vs Beto Fuentes', position: 1,
    prize: 'Voz de la Semana + Micrófono de Plata + 200 pts', prizeDescription: 'Badge Voz de la Semana + Micrófono de Plata + 200 puntos al Score',
    date: '2026-06-02', scoreEarned: 200,
    badges: ['ganador-semanal'],
    featured: false,
    wallType: ['landing', 'radio', 'programa'],
  },
  {
    id: 17, type: 'programa-vs-programa', name: 'Modo Creativo', photoUrl: 'https://readdy.ai/api/search-image?query=Creative%20arts%20radio%20program%20concept%20art%20with%20paintbrush%20and%20lightbulb%20illustration%2C%20colorful%20warm%20gradient%20design%2C%20artistic%20inspiration%20theme%2C%20clean%20modern%20visual%20style%2C%20creative%20energy%20aesthetic%2C%20editorial%20broadcast%20design&width=300&height=300&seq=winner-entity-modo-creativo&orientation=squarish',
    city: 'Chicago, IL', radioName: 'Radio Comunitaria Unida', programName: 'Modo Creativo',
    contestName: 'Batalla de Contenido: Modo Creativo vs Mañanas Argentinas', position: 1,
    prize: 'Programa Estrella + Contenido de Oro + 200 pts', prizeDescription: 'Badge Contenido de Oro + 200 puntos + destacado en todas las plataformas RFW',
    date: '2026-06-01', scoreEarned: 200,
    badges: ['ganador-semanal'],
    featured: false,
    wallType: ['landing', 'radio', 'programa'],
  },
  {
    id: 18, type: 'trivia', name: 'Lucía Fernanda Paz', photoUrl: winnerPhotos[14],
    city: 'San Diego, CA', radioName: 'Radio Internacional Voz', programName: '',
    contestName: 'Trivia Musical: Adivina la Canción en 3 Segundos', position: 1,
    prize: 'Parlante Bluetooth + 100 pts', prizeDescription: 'Parlante Bluetooth profesional + 100 puntos al Score RFW',
    date: '2026-06-01', scoreEarned: 100,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['app', 'radio'],
  },
  /* ─── ESTE AÑO (5 ganadores) ─── */
  {
    id: 19, type: 'radio-vs-radio', name: 'Deportes Total Radio', photoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20official%20emblem%20with%20soccer%20ball%20and%20trophy%20elements%2C%20dynamic%20dark%20blue%20and%20gold%20design%2C%20professional%20broadcast%20identity%20badge%2C%20energetic%20sports%20theme%2C%20polished%20vector%20emblem&width=300&height=300&seq=winner-entity-deportes-total&orientation=squarish',
    city: 'Dallas, TX', radioName: 'Deportes Total Radio', programName: '',
    contestName: 'Batalla del Norte: Deportes Total Radio vs Radio Comunitaria Unida', position: 1,
    prize: 'Campeón Semanal + Rey del Norte + 250 pts', prizeDescription: 'Badge Rey del Norte + 250 puntos extra + posición privilegiada en rankings',
    date: '2026-05-25', scoreEarned: 250,
    badges: ['ganador-semanal'],
    featured: false,
    wallType: ['landing', 'radio'],
  },
  {
    id: 20, type: 'club', name: 'Banda MX Army', photoUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20mexican%20music%20fan%20club%20celebration%20with%20colorful%20papel%20picado%2C%20festive%20atmosphere%20with%20warm%20amber%20and%20orange%20tones%2C%20traditional%20and%20modern%20fusion%20aesthetic%2C%20energetic%20community%20party%20vibe%2C%20event%20photography&width=300&height=300&seq=winner-entity-banda-army&orientation=squarish',
    city: 'San Antonio, TX', radioName: 'Banda Radio MX', programName: '',
    contestName: 'Duelo de Comunidades: Banda MX Army vs Comunidad Fe y Esperanza', position: 1,
    prize: 'Club Campeón + Comunidad Suprema + 150 pts', prizeDescription: 'Badge Comunidad Suprema + 150 puntos + todos los miembros reciben badge de campeón',
    date: '2026-04-15', scoreEarned: 150,
    badges: ['ganador-mensual'],
    featured: false,
    wallType: ['landing', 'club'],
  },
  {
    id: 21, type: 'concurso', name: 'Diana Carolina Vargas', photoUrl: winnerPhotos[15],
    city: 'Houston, TX', radioName: 'La Mexicana FM', programName: 'El Desparche',
    contestName: 'Concurso Mejor Programa de Radio 2026', position: 1,
    prize: 'Producción de Piloto de Radio + 500 pts', prizeDescription: 'Producción completa de piloto profesional + 500 puntos + asesoría de programación',
    date: '2026-03-20', scoreEarned: 500,
    badges: ['ganador-mensual'],
    featured: false,
    wallType: ['app', 'radio', 'programa'],
  },
  {
    id: 22, type: 'evento', name: 'Rafael Eduardo Molina', photoUrl: winnerPhotos[16],
    city: 'Los Ángeles, CA', radioName: 'Rock Total Radio', programName: '',
    contestName: 'Concierto Acústico Solidario: Sorteo Mayor', position: 1,
    prize: 'Guitarra Autografiada + 400 pts', prizeDescription: 'Guitarra eléctrica autografiada por artistas del festival + 400 puntos al Score',
    date: '2026-02-14', scoreEarned: 400,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['landing', 'app', 'radio'],
  },
  {
    id: 23, type: 'locutor-vs-locutor', name: 'Ricky Navarro', photoUrl: winnerPhotos[17],
    city: 'San Antonio, TX', radioName: 'Banda Radio MX', programName: '',
    contestName: 'Final Mensual de Locutores: Ricky Navarro vs Diego Quintero', position: 1,
    prize: 'Locutor del Mes + Micrófono de Platino + 500 pts', prizeDescription: 'Badge Locutor del Mes + Micrófono de Platino + 500 puntos + entrevista exclusiva',
    date: '2026-01-28', scoreEarned: 500,
    badges: ['ganador-mensual', 'campeon-rfw'],
    featured: false,
    wallType: ['landing', 'radio'],
  },
  /* ─── HISTÓRICO (7 ganadores) ─── */
  {
    id: 24, type: 'radio-vs-radio', name: 'La Mexicana FM', photoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20official%20emblem%20with%20guitar%20and%20sombrero%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20professional%20broadcast%20identity%20badge%2C%20festive%20vibrant%20style%2C%20polished%20vector%20emblem%20design&width=300&height=300&seq=winner-entity-mexicana-fm-2&orientation=squarish',
    city: 'Houston, TX', radioName: 'La Mexicana FM', programName: '',
    contestName: 'Gran Torneo Anual RFW 2025: Radio del Año', position: 1,
    prize: 'Radio del Año + Trofeo Diamante + 2000 pts', prizeDescription: 'Máximo galardón RFW + 2000 puntos + Salón de la Fama + features premium por 1 año',
    date: '2025-12-31', scoreEarned: 2000,
    badges: ['campeon-rfw', 'leyenda-rfw', 'ganador-mensual'],
    featured: true,
    wallType: ['landing', 'radio', 'app'],
  },
  {
    id: 25, type: 'programa-vs-programa', name: 'Tardes con Vale', photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20afternoon%20talk%20show%20concept%20art%20with%20golden%20hour%20lighting%2C%20cozy%20radio%20studio%20aesthetic%2C%20microphone%20and%20coffee%20cup%20on%20desk%2C%20soft%20peach%20and%20amber%20tones%2C%20intimate%20broadcast%20environment%2C%20inviting%20warm%20atmosphere%2C%20editorial%20style&width=300&height=300&seq=winner-entity-tardes-vale-2&orientation=squarish',
    city: 'Santa Rosa, CA', radioName: 'Radio Nueva Actitud', programName: 'Tardes con Vale',
    contestName: 'Final Mensual de Programas: Tardes con Vale vs El Desparche', position: 1,
    prize: 'Programa del Mes + Rating de Platino + 500 pts', prizeDescription: 'Badge Rating de Platino + 500 puntos + destacado en todas las plataformas RFW',
    date: '2025-11-30', scoreEarned: 500,
    badges: ['ganador-mensual', 'campeon-rfw'],
    featured: false,
    wallType: ['landing', 'radio', 'programa'],
  },
  {
    id: 26, type: 'club', name: 'La Mexicana Fan Club', photoUrl: 'https://readdy.ai/api/search-image?query=Elite%20mexican%20music%20fan%20club%20official%20crest%20with%20eagle%20and%20serpent%20symbol%2C%20luxurious%20gold%20and%20green%20color%20scheme%2C%20prestigious%20community%20identity%20badge%2C%20regal%20circular%20design%2C%20premium%20club%20emblem&width=300&height=300&seq=winner-entity-mexicana-club&orientation=squarish',
    city: 'Houston, TX', radioName: 'La Mexicana FM', programName: '',
    contestName: 'Superclásico de Clubes 2025: Campeón Anual', position: 1,
    prize: 'Club Supremo 2025 + Trono de la Comunidad + 1000 pts', prizeDescription: 'Badge Trono de la Comunidad + 1000 puntos repartidos entre miembros + perks VIP',
    date: '2025-10-15', scoreEarned: 1000,
    badges: ['campeon-rfw', 'leyenda-rfw', 'ganador-mensual'],
    featured: false,
    wallType: ['landing', 'club', 'app'],
  },
  {
    id: 27, type: 'concurso', name: 'Javier Alejandro Ponce', photoUrl: winnerPhotos[18],
    city: 'Miami, FL', radioName: 'Radio Fe y Esperanza', programName: '',
    contestName: 'Batalla de Freestyle RFW 2025: Campeón Nacional', position: 1,
    prize: 'Gira de 5 Presentaciones + 800 pts', prizeDescription: 'Gira por 5 ciudades + 800 puntos al Score + badge Campeón RFW + producción musical',
    date: '2025-08-20', scoreEarned: 800,
    badges: ['campeon-rfw', 'ganador-mensual'],
    featured: false,
    wallType: ['landing', 'app', 'radio'],
  },
  {
    id: 28, type: 'encuesta', name: 'Patricia Elena Ríos', photoUrl: winnerPhotos[19],
    city: 'Chicago, IL', radioName: 'Radio Comunitaria Unida', programName: 'Modo Creativo',
    contestName: 'Gran Encuesta Nacional RFW 2025: Voz del Oyente', position: 1,
    prize: 'Invitación a Junta Directiva RFW + 300 pts', prizeDescription: 'Participación en junta directiva anual + 300 puntos + badge Voz del Oyente',
    date: '2025-06-15', scoreEarned: 300,
    badges: ['ganador-mensual'],
    featured: false,
    wallType: ['app', 'radio'],
  },
  {
    id: 29, type: 'evento', name: 'Roberto Carlos Jiménez', photoUrl: winnerPhotos[20],
    city: 'Phoenix, AZ', radioName: 'La Kalle 99.9', programName: '',
    contestName: 'Encuentro de Locutores 2025: Premio Especial del Público', position: 1,
    prize: 'Curso de Locución Profesional + 350 pts', prizeDescription: 'Curso completo de locución profesional certificado + 350 puntos al Score',
    date: '2025-04-10', scoreEarned: 350,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['app', 'radio'],
  },
  {
    id: 30, type: 'trivia', name: 'Daniela María Soto', photoUrl: winnerPhotos[21],
    city: 'Dallas, TX', radioName: 'Deportes Total Radio', programName: 'Deporte Total',
    contestName: 'Trivia Deportiva: Campeones Mundiales 2024', position: 1,
    prize: 'Camiseta Oficial Autografiada + 100 pts', prizeDescription: 'Camiseta oficial de la selección autografiada + 100 puntos al Score RFW',
    date: '2025-02-28', scoreEarned: 100,
    badges: ['primer-ganador'],
    featured: false,
    wallType: ['app', 'radio', 'programa'],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STATS
   ═══════════════════════════════════════════════════════════════════ */

export const winnerGlobalStats = {
  totalWinners: 4238,
  winnersToday: 12,
  winnersThisWeek: 84,
  winnersThisMonth: 342,
  totalPointsAwarded: 847600,
  totalPrizesValue: 124500,
  activeCompetitions: 8,
  mostWinningRadio: 'La Mexicana FM',
  mostWinningProgram: 'Tardes con Vale',
  mostWinningHost: 'Ricky Navarro',
  leyendasCount: 3,
  campeonesCount: 12,
};

/* ═══════════════════════════════════════════════════════════════════
   HELPER FUNCTIONS
   ═══════════════════════════════════════════════════════════════════ */

export function getAllWinners(): RfwWinner[] {
  return allWinners;
}

export function getWinnersByType(type: WinnerType | 'todos'): RfwWinner[] {
  if (type === 'todos') return allWinners;
  return allWinners.filter((w) => w.type === type);
}

export function getWinnersByTimeFilter(filter: WinnerTimeFilter): RfwWinner[] {
  const now = new Date('2026-06-21');
  return allWinners.filter((w) => {
    const d = new Date(w.date);
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    switch (filter) {
      case 'hoy': return diffDays === 0;
      case 'semana': return diffDays <= 7;
      case 'mes': return diffDays <= 31;
      case 'ano': return diffDays <= 365;
      case 'historico': return true;
      default: return true;
    }
  });
}

export function getLatestWinners(limit: number = 4): RfwWinner[] {
  return [...allWinners].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, limit);
}

export function getFeaturedWinners(): RfwWinner[] {
  return allWinners.filter((w) => w.featured);
}

export function getWinnersOfTheMonth(): RfwWinner[] {
  return allWinners.filter((w) => {
    const d = new Date(w.date);
    return d.getMonth() === 5 && d.getFullYear() === 2026 && w.position === 1;
  }).slice(0, 6);
}

export function getWinnersByWall(wall: WallType): RfwWinner[] {
  return allWinners.filter((w) => w.wallType.includes(wall));
}

export function getWinnerById(id: number): RfwWinner | undefined {
  return allWinners.find((w) => w.id === id);
}

export function getWinnerBadgeInfo(badge: WinnerBadge): WinnerBadgeConfig | undefined {
  return winnerBadgeConfigs.find((b) => b.id === badge);
}

export function formatWinnerDate(dateStr: string): string {
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const d = new Date(dateStr);
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function getRelativeTime(dateStr: string): string {
  const now = new Date('2026-06-21');
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffDays < 31) return `Hace ${Math.floor(diffDays / 7)} semanas`;
  if (diffDays < 365) return `Hace ${Math.floor(diffDays / 31)} meses`;
  return `Hace ${Math.floor(diffDays / 365)} años`;
}

export function getPositionLabel(position: number): { label: string; emoji: string; color: string } {
  switch (position) {
    case 1: return { label: '1er Lugar', emoji: '🥇', color: 'text-yellow-500' };
    case 2: return { label: '2do Lugar', emoji: '🥈', color: 'text-stone-400' };
    case 3: return { label: '3er Lugar', emoji: '🥉', color: 'text-amber-700' };
    default: return { label: `${position}° Lugar`, emoji: '🎖️', color: 'text-stone-500' };
  }
}